import { computed, effect, untracked } from '@angular/core';

/**
 * Runs `load` whenever `key` settles on a new truthy value - and only then.
 *
 * Meant for store `onInit` hooks whose load condition depends on the active
 * user (permissions, context) plus the commune. Reading those signals
 * directly inside an `effect()` would re-run it on every new `user()`
 * reference (profile save, re-fetch), even when the outcome didn't change.
 * Funnelling the condition through a `computed` with a primitive key dedupes
 * that, and `untracked` keeps signals read inside `load` from becoming
 * dependencies of their own.
 *
 * Must be called in an injection context (e.g. `withHooks` -> `onInit`).
 *
 * @param {() => string | false | null | undefined} key - Falsy = don't load; a changed truthy value = (re)load.
 * @param {() => unknown} load - The load call to run.
 * @return {void} Does not return a value.
 */
export function loadWhen(
  key: () => string | false | null | undefined,
  load: () => unknown
): void {
  const loadKey = computed(key);
  effect(
    () => {
      if (loadKey()) {
        untracked(() => load());
      }
    },
    { allowSignalWrites: true }
  );
}
